import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useFinanzas } from "../../context/FinanzasContext";
import { useUsers } from "../../context/UsersContext";
import { FaSpinner, FaArrowLeft } from "react-icons/fa"; // Ícono de carga y flecha

const PagosForm = () => {
  const { id } = useParams();
  const [formData, setFormData] = useState({
    students_id: "",
    student_name: "", // Para mostrar el nombre del estudiante
    monto: "",
    fecha: new Date().toISOString().split("T")[0], // Fecha actual como valor por defecto
    razon_pago: "",
  });

  const { fetchPagoById, addPago, editPago } = useFinanzas();
  const { students, fetchStudents } = useUsers(); // Obtener lista de estudiantes
  const navigate = useNavigate();
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [searchValue, setSearchValue] = useState(""); // Controla el input de búsqueda
  const [isLoading, setIsLoading] = useState(false); // Nuevo estado de carga

  // Memoizar fetchStudents
  const fetchStudentsMemo = useCallback(fetchStudents, [fetchStudents]);

  // Memoizar loadPago
  const loadPago = useCallback(async () => {
    if (id) {
      try {
        const pago = await fetchPagoById(id);
        setFormData({
          students_id: pago.students_id || "",
          student_name: `${pago.first_name} ${pago.second_name} ${pago.first_surname} ${pago.second_surname}`, // Nombre completo
          monto: pago.monto || "",
          fecha: pago.fecha?.split("T")[0] || new Date().toISOString().split("T")[0], // Fecha actual como valor por defecto
          razon_pago: pago.razon_pago || "",
        });
        setSearchValue(`${pago.first_name} ${pago.second_name} ${pago.first_surname} ${pago.second_surname}`); // Sincronizar con la búsqueda
      } catch (error) {
      }
    }
  }, [id, fetchPagoById]);

  // useEffect para cargar estudiantes y datos del pago (si aplica)
  useEffect(() => {
    fetchStudentsMemo(); // Cargar lista de estudiantes al iniciar
    loadPago(); // Cargar datos del pago si hay un ID
  }, [fetchStudentsMemo, loadPago]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleStudentSearch = (e) => {
    const searchTerm = e.target.value.toLowerCase();
    setSearchValue(searchTerm);

    if (searchTerm.length > 0) {
      const results = students.filter(
        (student) =>
          student.user_id.toString().includes(searchTerm) || // Por ID
          `${student.first_name} ${student.second_name} ${student.first_surname} ${student.second_surname}`
            .toLowerCase()
            .includes(searchTerm) // Por nombre completo
      );
      setFilteredStudents(results);
    } else {
      setFilteredStudents([]);
    }
  };

  const handleStudentSelect = (student) => {
    setFormData({
      ...formData,
      students_id: student.user_id,
      student_name: `${student.first_name} ${student.second_name} ${student.first_surname} ${student.second_surname}`,
    });
    setFilteredStudents([]);
    setSearchValue(`${student.first_name} ${student.second_name} ${student.first_surname} ${student.second_surname}`); // Sincronizar input con el nombre
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true); // Activar animación de carga
    try {
      if (id) {
        await editPago(id, { ...formData, student_name: undefined }); // No enviar `student_name`
      } else {
        await addPago({ ...formData, student_name: undefined });
      }
      navigate("/finanzas");
    } catch (error) {
    } finally {
      setIsLoading(false); // Desactivar animación de carga
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col items-center p-4">
      <button
        className="self-start flex items-center bg-main-bg-sidebar rounded-full hover:bg-blue-800 text-white font-bold px-4 py-2 mb-4"
        onClick={() => navigate(-1)}
      >
        <FaArrowLeft className="mr-2" />
        Volver
      </button>
      <form onSubmit={handleSubmit} className="bg-white rounded shadow w-full max-w-2xl">
        <div className="bg-main-bg-sidebar text-white p-3 rounded-t">
          <h2 className="text-xl font-bold">
            {id ? "Editar Pago" : "Nuevo Pago"}
          </h2>
        </div>
        <div className="p-4 grid grid-cols-1 gap-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Buscar estudiante por ID o nombre"
              value={searchValue}
              onChange={handleStudentSearch}
              className="border rounded p-2 w-full"
              required={!id}
            />
            {filteredStudents.length > 0 && (
              <ul className="absolute z-10 bg-white border border-gray-300 rounded shadow-md max-h-40 overflow-y-auto w-full">
                {filteredStudents.map((student) => (
                  <li
                    key={student.user_id}
                    className="p-2 hover:bg-gray-100 cursor-pointer"
                    onClick={() => handleStudentSelect(student)}
                  >
                    {student.user_id} - {student.first_name} {student.second_name} {student.first_surname} {student.second_surname}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <input
            type="number"
            name="monto"
            placeholder="Monto"
            value={formData.monto}
            onChange={handleInputChange}
            required
            className="border rounded p-2 w-full"
          />
          <input
            type="date"
            name="fecha"
            value={formData.fecha}
            onChange={handleInputChange}
            max={new Date().toISOString().split("T")[0]} // Restringir fechas futuras
            required
            className="border rounded p-2 w-full"
          />
          <input
            type="text"
            name="razon_pago"
            placeholder="Razón del Pago"
            value={formData.razon_pago}
            onChange={handleInputChange}
            required
            className="border rounded p-2 w-full"
          />
        </div>
        <button
          type="submit"
          className="ml-4 mb-4 mt-4 bg-main-blue text-white px-4 py-2 rounded-full hover:bg-blue-800 "
          disabled={isLoading}
        >
          {isLoading ? <FaSpinner className="animate-spin mr-2" /> : id ? "Actualizar" : "Guardar"}
        </button>
      </form>
    </div>
  );
};

export default PagosForm;